/**
 * Shapes the API returns. Defined once and imported by the API, both
 * frontends and any worker, so a field rename can never leave one side
 * reading something the other stopped sending.
 */
import { PlanCode, TakedownChannel, type BillingInterval } from './enums';
import type {
  AccountKind,
  AccountRole,
  AuthorizationStatus,
  ChannelThreadStatus,
  CheckState,
  DomainCaseStatus,
  InviteStatus,
  InvoiceStatus,
  Liveness,
  ScanMode,
  DepthReason,
  ScanRunStatus,
  SearchMode,
  SpendReason,
  SupportChannel,
  SupportRequestType,
  SupportThreadStatus,
  SupportTicketStatus,
  WhoisOutcome,
  WhoisReason,
} from './enums';
import type { PlanCardRow } from './plan-card';
import type { ExposureCounts } from './revenue-risk';
import type { CreatorAttention, ProtectionState } from './agency-overview';
import type { TargetSearchScope } from './search-compare-defaults';
import type { ProbeCheck, ProbeRecord } from './probe';
import type { ReferralTerms } from './referrals';
import type { ClientStepAt } from './client-journey';
import type { ReferralRewardStatus, ReferralStatus } from './enums';

export interface Entitlements {
  scanFrequency: 'once' | 'weekly' | 'daily';
  usernamesIncluded: number;
  profilesIncluded: number;
  /** The fewest profiles an agency plan is sold with; 0 on a creator plan. */
  minProfiles: number;
  canTakedown: boolean;
  /** Complexity points a month. Null means unlimited: Infinity does not survive JSON. */
  pointsPerMonth: number | null;
  whitelisting: boolean;
  sources: string[];
  reports: 'none' | 'monthly' | 'weekly';
  priority: 0 | 1 | 2;
  assignments: boolean;
  whiteLabel: boolean;
  apiAccess: boolean;
}

/** One result in the client command palette: a profile, a removal or a detection. */
export interface PaletteHit {
  kind: 'profile' | 'removal' | 'detection';
  title: string;
  sub: string | null;
  href: string;
}

export interface PlanOption extends Entitlements {
  code: string;
  kind: 'creator' | 'agency';
  /** Monthly base price; an agency adds `perProfileUsd` for every profile. */
  priceUsd: number | null;
  perProfileUsd: number | null;
  /** Annual base price for a creator plan's yearly billing; null for agencies
   *  and for a plan nobody has priced yearly yet. */
  annualUsd: number | null;
  /** Scale is agreed with sales rather than bought at checkout. */
  salesOnly: boolean;
  /** The rows of a creator plan's public card; null for an agency plan. */
  cardRows: PlanCardRow[] | null;
}

/**
 * The public plan catalogue, unauthenticated, at the same prices
 * `/billing/checkout` charges — both read `PricingService`, which reads only the
 * database. The extra-username fee sits beside the list rather than on every
 * row: one fee, not a plan property, but needed in the same request.
 */
export interface PlansCatalogue {
  plans: PlanOption[];
  /** Per-extra-name monthly price a manager set; null until one is set. */
  extraUsernameUsd: number | null;
}

/** Agency that pays for a linked SOLO creator, when one does. */
export interface ManagedByAgency {
  id: string;
  /** Display name on the plan and help banners. */
  name: string;
}

/** What GET /plans/me returns for the signed-in account. */
export interface MyPlan {
  /** Null without a subscription. */
  planCode: PlanCode | null;
  entitlements: Entitlements;
  usernameAllowance: number;
  /** Per-extra-name monthly price a manager set; null until one is set. */
  extraUsernameUsd: number | null;
  /** True once Stripe reports the subscription will not renew past `cancelAt`. */
  cancelAtPeriodEnd: boolean;
  /** When access actually ends, only meaningful alongside `cancelAtPeriodEnd`. */
  cancelAt: string | null;
  /** When the current paying cycle began; billing runs a flat 30 days from
   * here (see billing.service.ts). Null on an account that never paid. */
  billingPeriodStartedAt: string | null;
  /**
   * Complexity points spent this billing period.
   *
   * Zero until somebody files. It exists because the screen that shows the
   * monthly allowance had no way to say how much of it was left, and printed
   * an invented "14" to every account for as long as nothing counted.
   */
  pointsUsed: number;
  /** Profiles the account may hold now: the plan's, or what an agency bought. */
  profileAllowance: number;
  /** Profiles an agency pays for; 0 on a creator plan. */
  profileSeats: number;
  /**
   * When this SOLO account was opened from an agency invitation that is still
   * linked: Stripe checkout/cancel stay with the agency. Null for everyone else.
   */
  managedByAgency: ManagedByAgency | null;
}

/**
 * The card Stripe will charge next, as the billing page names it. Only what a
 * person needs to recognise which card it is — never a token, never anything
 * that could be used to charge it.
 */
export interface BillingCard {
  /** Stripe's own brand slug ('visa', 'mastercard'), titled for display by the caller. */
  brand: string;
  last4: string;
  expMonth: number;
  expYear: number;
}

/** One invoice Stripe has on file for this account, newest first. */
export interface BillingInvoice {
  id: string;
  /** When it was paid, falling back to when it was raised on one still open. */
  date: string;
  /** The invoice's own first line, which is the plan it paid for. */
  description: string;
  amountUsd: number;
  status: InvoiceStatus;
  /** Stripe's hosted PDF. Null on a draft, which has none yet. */
  pdfUrl: string | null;
}

/** The live subscription as the billing page states it, read from Stripe. */
export interface BillingSubscription {
  /** 'month' or 'year' — the cycle this plan renews on. */
  interval: BillingInterval;
  /** ISO currency the account is billed in (e.g. 'usd'), not assumed. */
  currency: string;
  /**
   * What the next renewal will charge, in the billing currency. Null when the
   * subscription is set to cancel at period end: nothing more will be taken.
   */
  nextChargeAmount: number | null;
  /** When the current period ends — the next charge date, or the access-until date when cancelling. */
  currentPeriodEnd: string;
  /** Stripe's own status slug (active, past_due, trialing, …). */
  status: string;
}

/**
 * What GET /billing/summary returns: everything the billing page shows that is
 * not the plan itself. Read live from Stripe, so an account with no customer
 * (never paid, or granted a plan straight in the database) comes back empty
 * rather than with anything invented to fill the table.
 */
export interface BillingSummary {
  card: BillingCard | null;
  invoices: BillingInvoice[];
  /** The live plan subscription, or null when there is none. */
  subscription: BillingSubscription | null;
  /** False when Stripe could not be reached at all, so the page can say so instead of showing "no invoices". */
  reachable: boolean;
}

/**
 * One account on the retention queue: a client who is about to churn or needs
 * attention, so support can act before access ends.
 */
export interface RetentionRow {
  accountId: string;
  /** Agency name, else the client's own name or email — whatever labels the row. */
  name: string;
  planCode: string | null;
  planLabel: string;
  /** Stripe billing status slug (past_due, unpaid, active, …). */
  billingStatus: string;
  /** True when the subscription is set to stop at the period end. */
  cancelAtPeriodEnd: boolean;
  /** ISO date the account loses access, or null when it cannot be told. */
  accessEndsAt: string | null;
  /** Whole days until accessEndsAt; negative once past, null when unknown. */
  daysLeft: number | null;
}

export interface Profile {
  id: string;
  stageName: string;
  /**
   * The one real name behind the work. Null until somebody types it, and the
   * first scan waits for it: a search that knows only handles finds the stolen
   * content and misses the doxxing published beside it.
   */
  legalName: string | null;
  /** Details that must never turn up next to this person's work. */
  phoneNumbers: string[];
  addresses: string[];
  /** Handles, always without a leading @. */
  usernames: string[];
  officialUrls: string[];
  /** Audience size, as one of FOLLOWER_RANGES. Null until somebody picks one. */
  followerRange: string | null;
  /**
   * How far signup's onboarding got for this profile: 0 not started, 1 on the
   * stage-name step, 2 on the real-name step, 3 finished. The single source the
   * popup resumes from — closed and reopened, it reads this, not the browser.
   */
  onboardingStep: number;
  authorizationStatus: AuthorizationStatus;
  /** Whether the first handle names a real OnlyFans page; null while unchecked or the platform did not answer. */
  handleExists?: boolean | null;
  lastScanAt: string | null;
  /** When watching began. Null means this profile has never been looked for. */
  firstScanQueuedAt: string | null;
  whitelist?: { id: string; domain: string }[];
  _count?: { detectedUrls: number };
}

/**
 * One finding opened up: what of the person is on that page, what we captured,
 * what has happened since. Every entry was matched, never inferred, and
 * `examined: false` means nobody has looked — not that a page was looked at and
 * found to carry nothing.
 */
export interface FindingDetail extends Finding {
  /**
   * Other domains the same operation runs. Removing this page does not remove
   * what is on these, and saying otherwise is the fastest way to lose trust.
   */
  mirrors: string[];
  whatWasFound: {
    stageName: boolean;
    legalName: boolean;
    aliases: string[];
    phoneNumbers: string[];
    addresses: string[];
    imageMatch: boolean;
    examined: boolean;
  };
  evidence: { id: string; sha256: string; capturedAt: string }[];
  profile: { id: string; stageName: string };
}

/**
 * One `DomainCaseUrl` join row, as a finding's own case list shows it — a
 * pointer to the domain case this URL belongs to, not the case's fields
 * inlined. A URL used to own a case outright (one row per URL per channel);
 * now it only ever points at one, so `case` is nested rather than flattened.
 * Channel-level detail (which thread, escalation) is staff-only and does not
 * appear here at all — open `case.id` in the case screen for that.
 */
export interface TakedownCaseSummary {
  /** The `DomainCaseUrl` row id, not the case's own id. */
  id: string;
  /** When this URL was added to the case. */
  addedAt: string;
  case: {
    id: string;
    /** Optional because the client's own screens are served a shape without
     *  it: she has one case per domain per round and never quotes a number to
     *  anybody. */
    ref?: number;
    num: number;
    domain: string;
    status: DomainCaseStatus;
    openedAt: string;
    resolvedAt: string | null;
  };
}

export interface Finding {
  id: string;
  /** Whose finding it is: needed to whitelist the host against that profile. */
  profileId: string;
  url: string;
  domain: string;
  /**
   * Addresses Google has taken off this site on everybody's complaints. A hundred
   * findings look alike and are not: one domain has had 9.5 million removed,
   * another embedded the wrong photo once. Null means we never looked it up,
   * which is not zero — one is about the site, the other about us.
   */
  siteUrlsRemoved?: number | null;
  /**
   * Her face on an account that is not hers, kept apart because the two are
   * answered differently: a tube site gets a copyright notice, an impersonator
   * gets reported as one. "Forty leaks" when six are people wearing her name
   * describes the wrong problem. "Possible" is honest — whether the account is a
   * fan, a mirror or a thief is a question for a person.
   */
  possibleImpersonation?: boolean;
  source: string;
  title: string | null;
  confidence: number | null;
  hasImageMatch: boolean;
  /**
   * How the match was made: `phash` when the fingerprint matched almost
   * exactly, `clip` when it survived a crop or a recolour. Null while nothing
   * has been checked. The customer's table names it, because the two are
   * different degrees of certainty to somebody deciding whether to file.
   */
  matchMethod: string | null;
  firstSeen: string;
  /**
   * When a sweep last saw this page, and how often. `timesSeen` above one is a
   * re-upload — the page came back after we had already found it once, which the
   * overview counted and the table could not point at.
   */
  lastSeen: string;
  timesSeen: number;
  /**
   * Whether the page is still there. The most important fact about a leak,
   * and no type declared it, so every screen showed the state of the *case*
   * instead — what we have done, not whether her material is online. Those
   * come apart exactly when it matters.
   */
  liveness: Liveness;
  checkState: CheckState;
  lastLivenessAt: string | null;
  /** When a search-delisting notice actually cleared this URL out of a search
   * index; null while it's still findable (or nobody has tried). Independent
   * of `liveness`: a page can be delisted and still online, or removed at
   * source and never delisted because nobody asked. */
  delistedAt: string | null;
  /** Support looked at this page and found nothing of hers on it. */
  declinedAt: string | null;
  cases: TakedownCaseSummary[];
  /**
   * True once the row's domain is whitelisted on its profile. Computed fresh per
   * list, never cached on the row: "This is mine" whitelists going forward
   * without touching findings already on screen, so this is what marks them
   * without a reload changing which rows exist.
   */
  whitelisted: boolean;
  /** True past the free plan's unlocked set — `url`/`title`/`cases` are
   *  already masked server-side when this is true, not only left undrawn. */
  locked: boolean;
}

export interface QueueCase {
  id: string;
  channel: TakedownChannel;
  status: ChannelThreadStatus;
  failureReason: string | null;
  /**
   * Set when approving this case would put it on a public record: Google
   * republishes every delisting request to Lumen and links it from the results
   * page, so for stolen intimate content that channel hands a curious searcher
   * the addresses we asked to hide. The reviewer is the last person who can
   * decide it is worth it.
   */
  publicRecordWarning?: string | null;
  /** The rung this case actually climbed from; null if it never escalated. */
  escalatedFrom: string | null;
  /**
   * How many addresses Google has already taken off this site; null when the
   * dump has nothing under this domain. Sent rather than kept on the server
   * because the queue is ordered by it: an order a reviewer cannot see the
   * reason for reads as arbitrary, and the next person sorts it by date again.
   */
  siteUrlsRemoved?: number | null;
  createdAt: string;
  detectedUrl: {
    id: string;
    url: string;
    domain: string;
    confidence: number | null;
    hasImageMatch: boolean;
    profile: { stageName: string; officialUrls: string[] };
  };
}

/**
 * What we know about the company behind one domain, and where to complain. A
 * case sits stuck for a week almost always because of a row of this shape with
 * both abuse addresses empty, which makes this a work queue rather than a
 * reference. A manual `contactSource` beats a refreshed lookup: somebody already
 * established the lookup was wrong.
 */
export interface ProviderRecord {
  id: string;
  domain: string;
  ip: string | null;
  asn: string | null;
  registrar: string | null;
  hoster: string | null;
  /** Set when the site hides behind a CDN, which changes who to complain to. */
  cdn: string | null;
  registrarAbuse: string | null;
  hosterAbuse: string | null;
  contactSource: 'provider' | 'manual' | string;
  /** How often complaints to this host end with the content gone. */
  successRate: number | null;
  avgResponseDays: number | null;
  lastCheckedAt: string | null;
  updatedAt: string;
}

/**
 * A {@link ProviderRecord}'s row on the Domains info screen, with the two
 * facts that screen adds: which mirror family it groups under (computed from
 * the name, unless somebody said otherwise) and how many cases already exist
 * against it.
 */
export interface DomainInfoRecord {
  id: string;
  domain: string;
  ip: string | null;
  asn: string | null;
  registrar: string | null;
  hoster: string | null;
  cdn: string | null;
  registrarAbuse: string | null;
  hosterAbuse: string | null;
  domainEmail: string | null;
  /** When the domain was registered and when it runs out, as WHOIS said: `YYYY-MM-DD`. */
  registeredAt: string | null;
  expiresAt: string | null;
  /** Which service's answer the dates came from: 'apilayer' or 'rdap'. */
  datesFrom: string | null;
  /** How Is CDN? was found: 'whois', 'cloudflare_ips', 'dns_ns' or 'dns' (DEC-038). */
  cdnSource: string | null;
  /** The CDN's abuse desk, when a person typed one in. */
  cdnAbuse: string | null;
  /** Who runs the site: its own address, then the owner addresses the WHOIS answer listed. */
  ownerEmails: string[];
  /** The site's own DMCA or copyright page, from our copy of its sitemap. */
  dmcaPage: string | null;
  /** A DMCA or copyright desk among the addresses we hold for this domain. */
  dmcaEmail: string | null;
  contactSource: 'provider' | 'manual' | string;
  /** Which paid WHOIS service answered, when nothing free would. Also the
   *  record of whose free allowance the answer cost. */
  whoisProvider: string | null;
  successRate: number | null;
  avgResponseDays: number | null;
  lastCheckedAt: string | null;
  updatedAt: string;
  /** When this domain joined the catalogue (DEC-073). Null for a row the
   *  catalogue has never actually seen — WHOIS/case activity reached us
   *  before our own sweep did. */
  createdAt: string | null;
  /** A person confirmed this domain has somewhere to send a notice. */
  approved: boolean;
  /** The group's key: another domain's stem when joined by hand, otherwise
   *  this domain's own name minus its TLD. */
  mirrorGroup: string;
  /** True for every member but the one this batch saw first. */
  isMirror: boolean;
  /** Family size, including this row — 1 means it stands alone. */
  mirrorCount: number;
  /** Cases already filed against this domain, across every client. */
  cases: number;
  /** Links still up behind this door's cases: what the work actually is,
   *  which a count of cases does not say. */
  liveLinks: number;
  /** When this door last answered anything of ours, ever. */
  lastReplyAt: string | null;
  /** The last letter to it bounced. An address that bounces reads exactly like
   *  a working one in a column of addresses, which is why it is a field. */
  bounced: boolean;
  /** Which shelf of the atlas this domain sits on, and whether its own search
   *  can be used: `works`, `manual` (up, but not this way), `dead`, `unknown`. */
  category: string | null;
  searchState: string | null;
  searchTemplate: string | null;
  /** What the site publishes about itself, read weekly. */
  hasRobots: boolean | null;
  hasSitemap: boolean | null;
  /** Pages in our copy of the sitemap; null until it has been read. */
  sitemapPages: number | null;
  sitemapUrl: string | null;
  sitemapReadAt: string | null;
}

export interface EvidenceItem {
  id: string;
  sha256: string;
  capturedAt: string;
  screenshotUrl: string;
  htmlUrl: string;
}

export const AUTOMATION_EVIDENCE_QUEUE = 'automation-evidence';
export const AUTOMATION_DELISTING_QUEUE = 'automation-delisting';
/**
 * The public scan's paid step, and the ticket a waiting page polls. A queue and
 * not a map in the HTTP process: the ticket must outlive the request and be
 * readable from whichever process the next poll lands on. `jobId` is the folded
 * handle, so BullMQ refusing a duplicate id is the lock against buying one name
 * twice.
 */
export const PUBLIC_SCAN_QUEUE = 'public-scan';
/**
 * Taking a client's avatar off her own page with a real browser. A plain fetch
 * comes first and answers for most pages; the platforms a fake account is built
 * to imitate sit behind a challenge and refuse it — exactly the profiles that
 * would otherwise have nothing to match against. Second attempt lands here, in
 * the process that already has Playwright.
 */
export const AUTOMATION_AVATAR_QUEUE = 'automation-avatar';

export interface CaptureAvatarJobData {
  profileId: string;
}

/**
 * Does the profile's public bio carry the Snily notice? A browser reads the page
 * (OnlyFans serves the bio only to scripts), so the job lives in automation.
 */
export const AUTOMATION_OWNERSHIP_QUEUE = 'automation-ownership';

/** What started a bio-notice check. Stored as the value, shown through OWNERSHIP_TRIGGER_LABEL. */
export const OwnershipTrigger = {
  /** The profile was just added. */
  CREATED: 'created',
  /** Its handle or official page was edited, so the old proof no longer applies. */
  HANDLE_CHANGED: 'handle_changed',
  /** The weekly pass over paying clients' profiles. */
  SWEEP: 'sweep',
  /** The client pressed the button on her own profile. */
  CLIENT: 'client',
  /** Somebody on the team pressed it in the panel. */
  STAFF: 'staff',
} as const;
export type OwnershipTrigger = (typeof OwnershipTrigger)[keyof typeof OwnershipTrigger];

export const OWNERSHIP_TRIGGER_LABEL: Record<OwnershipTrigger, string> = {
  created: 'System: profile added',
  handle_changed: 'System: handle changed',
  sweep: 'System: weekly check',
  client: 'Client',
  staff: 'Staff',
};

export interface VerifyOwnershipJobData {
  profileId: string;
  trigger: OwnershipTrigger;
}

/**
 * The notice a client puts in her public bio. One text for everybody: it says
 * the profile is protected here, and its presence on her page is the proof she
 * runs the page. Changing a word of it means every client edits her bio again.
 */
export const OWNERSHIP_NOTICE =
  'This profile and its content are protected by Snily. Any unauthorized copying, sharing, redistribution, or commercial use of photos, videos, or audio without prior written consent is strictly prohibited and may result in legal action.';

/**
 * What a page must contain for the notice to count as there. Only the first
 * sentence, compared without case, punctuation or spacing: a platform is free
 * to wrap, trim or re-quote a bio, and a check that insists on every character
 * would call a present notice missing.
 */
export function noticeNeedle(): string {
  return normaliseNotice('This profile and its content are protected by Snily');
}

/** Lowercase letters and digits only, so quotes, dashes and line breaks never matter. */
export function normaliseNotice(text: string): string {
  return text.toLowerCase().replace(/[^a-z0-9]+/g, '');
}

/** Whether a page's visible text carries the notice. */
export function pageHasNotice(pageText: string): boolean {
  return normaliseNotice(pageText).includes(noticeNeedle());
}

/** Where a bio-code check is, in the order a client watches it move. */
export const OwnershipStage = {
  QUEUED: 'queued',
  OPENING: 'opening',
  READING: 'reading',
  DONE: 'done',
  /** OnlyFans itself did not answer, so nothing was concluded about the profile. */
  UNREACHABLE: 'unreachable',
} as const;
export type OwnershipStage = (typeof OwnershipStage)[keyof typeof OwnershipStage];

export interface OwnershipStatus {
  /** The notice to put in the bio. */
  text: string;
  /** When the notice was last seen on the page. */
  verifiedAt: string | null;
  /** When a browser last looked and reached the platform. */
  checkedAt: string | null;
  /** The notice was there once and the latest look did not find it. */
  lost: boolean;
  /** A check is waiting or running right now, so a reload still shows it working. */
  checking: boolean;
  /** Where it is, when the stream just said so; null on a plain read. */
  stage: OwnershipStage | null;
}

/** One profile's bio-code check, for the panel's list. */
export interface OwnershipRow {
  id: string;
  modelNo: number;
  stageName: string;
  /** The handle the browser reads, or null when there is nothing to read. */
  handle: string | null;
  accountId: string;
  clientNo: number;
  clientName: string;
  clientKind: 'solo' | 'agency';
  /** `never`: nobody asked yet. `missing`: looked, and it was never there. `lost`: it was there and is gone now. */
  state: 'verified' | 'lost' | 'missing' | 'never';
  checkedAt: string | null;
  verifiedAt: string | null;
  /** When the profile was added: a profile days old with no check is normal, one months old is not. */
  createdAt: string;
  /** The signed letter's own state, the other half of "is this really hers". */
  authorizationStatus: string;
  planCode: string | null;
  /** What started the last check; null on one that predates the column. */
  trigger: OwnershipTrigger | null;
  /** A browser has been asked and has not answered yet, so a reload still shows it working. */
  checking: boolean;
}

/**
 * The crawl's second attempt: a page that came back Cloudflare's or
 * DDoS-Guard's "checking your browser" page, rendered by a real browser so
 * its own script can run out the wait a plain request can't. Not a
 * Turnstile/CAPTCHA solver — still a challenge afterward stays failed.
 */
export const AUTOMATION_CRAWL_QUEUE = 'automation-crawl';

export interface CrawlFetchJobData {
  url: string;
}

export interface CrawledPage {
  status: number;
  html: string;
  /** The clearance a render bought, ready to send as a `Cookie` header. Null
   *  when nothing outlives the browser — session-only cookies, or a page
   *  still showing a challenge when this returns. */
  cookieHeader: string | null;
  /** When the cookie above stops being worth sending, the earliest expiry
   *  among the cookies it is built from. Null exactly when `cookieHeader` is. */
  cookieExpiresAt: string | null;
}

export interface CaptureEvidenceJobData {
  detectedUrlId: string;
}

export interface CapturedEvidence {
  screenshotKey: string;
  htmlKey: string;
  sha256: string;
  capturedAt: string;
}

export interface PrepareDelistingFormJobData {
  engineId: string;
  formUrl: string;
  request: {
    infringingUrl: string;
    originalUrls: string[];
    rightsHolder: string;
    requesterEmail: string;
  };
}

export interface DelistingOutcome {
  engine: string;
  method: 'submitted' | 'emailed' | 'manual';
  reference: string | null;
  note?: string;
  /**
   * The form an operator opens to finish a 'manual' engine. Carried through so
   * the support worklist is a direct link, not a name the operator has to go
   * and look up.
   */
  formUrl?: string;
  /** How many fields the automation pre-filled before it hit the wall. */
  prefilled?: number;
}

/**
 * A signed letter waiting on a person to read it — the one queue that stops the
 * product working when nobody empties it: `authorizationStatus` must read
 * `verified` before a notice can be filed, and only a reviewer accepting one of
 * these writes that word.
 */
export interface WaitingLetter {
  id: string;
  /** "MD-4412", the number a colleague says out loud. */
  modelNo: number;
  stageName: string;
  /** The name on the signature, which is the thing being checked against. */
  legalName: string | null;
  accountId: string;
  /** Whose account she is on, and on what terms: a free trial and a paying
      agency are not the same letter to leave waiting. */
  /** "CL-2041" / "AG-118", the account's own number. */
  clientNo: number;
  clientName: string;
  clientKind: 'solo' | 'agency';
  planCode: string | null;
  billingStatus: string;
  /** Who looks after her, and who is standing in while they are away. */
  supportName: string | null;
  coveringName: string | null;
  /** What the letter is holding up: cases that cannot be filed, and links
      already found and sitting there. */
  openCases: number;
  foundUrls: number;
  /** The document itself, as stored. A 20 KB scan is an unreadable one. */
  letterBytes: number | null;
  letterSha256: string | null;
  /** How many times this letter has already been sent back. */
  sentBack: number;
  authorizationUploadedAt: string | null;
  authorizationStatus: AuthorizationStatus;
}

/**
 * One resolved abuse contact for a domain, as the Contacts tab shows it.
 * `type` is `TakedownChannel`-shaped (`domain_email`/`hoster_abuse`/
 * `registrar_abuse`/`cdn_abuse`). `source` says where it came from
 * (`provider`, the paid WHOIS, or `manual`); `verified` is true once the address
 * has actually taken a delivered reply, same idea the mockup used.
 */
export interface DomainContact {
  type: string;
  value: string;
  org: string | null;
  source: string;
  verified: boolean;
}

/**
 * A domain's whole contact picture — one row, reused by every case ever
 * opened on this domain. Built from `ProviderIntel` plus its manual
 * overrides; never the raw Prisma row, which carries RDAP internals
 * (`dumpRequests`, …) that are not this screen's business.
 */
export interface DomainContactsResponse {
  domain: string;
  asn: string | null;
  cdn: string | null;
  /** Days since the row was last checked against WHOIS, or null if never. */
  whoisAge: number | null;
  contacts: DomainContact[];
}

/**
 * One channel's correspondence thread within a domain case. `dmcaGateOpen` is
 * carried on the case, not here, because it depends on every other thread at
 * once.
 */
export interface ChannelThreadSummary {
  channel: TakedownChannel;
  status: ChannelThreadStatus;
  contact: string | null;
  externalCaseId: string | null;
  nextActionAt: string | null;
  sentAt: string | null;
  resolvedAt: string | null;
  failureReason: string | null;
  /** Present only when the thread has messages; the panel opens it lazily. */
  messageCount: number;
  /**
   * A rung further down the chain than the one being worked: an address
   * resolves (or none is needed) but its turn has not come. Drafted is for the
   * channel somebody is on — a case showing four channels "drafted" at once
   * reads as four letters waiting to go out when there is one.
   */
  later?: boolean;
}

/** One URL inside a domain case, with its own independent liveness/serp state. */
export interface DomainCaseUrlSummary {
  id: string;
  url: string;
  path: string;
  firstSeen: string;
  liveness: Liveness;
  delistedAt: string | null;
  /** Which channels' notices this URL actually went out in. */
  sentInChannels: string[];
  /** Gone, and on the re-upload schedule (`goneAt` set). Absent from older fixtures. */
  watching?: boolean;
}

/**
 * One model a support member works, for the step between signing in and
 * opening a case.
 *
 * The console used to be one flat list of every case belonging to everybody,
 * which is not how the work is actually organised: a support member holds
 * particular clients, picks one, and works that client's cases. `openCases`
 * is what makes this a queue rather than a list of names — picking who to
 * work means picking the one with something waiting.
 */
export interface AssignedModel {
  profileId: string;
  stageName: string;
  accountId: string;
  /** The company, when the model belongs to an agency rather than herself. */
  accountName: string | null;
  accountKind: string | null;
  openCases: number;
  /** Somebody is standing in on this model, so today it is theirs. */
  coveredBy: string | null;
  /** True when the reader is the stand-in rather than the owner. */
  covering: boolean;
  /**
   * Named when this is the reader's own client and somebody else is covering
   * it: the row is listed so they can see where their client went, and marked
   * so they know it is not theirs to touch today.
   */
  lentTo?: string | null;
  /**
   * Who has this client open right this minute, when it is somebody other
   * than the reader — the stand-in, usually. Null for nobody, and null for
   * the reader's own tab, where "taken by you" would be noise.
   */
  openBy?: string | null;
}

/**
 * One client, and how her removals are going.
 *
 * Five numbers rather than one, because "twelve cases" says nothing a
 * supervisor can act on: twelve in progress is a busy week, twelve parked is
 * somebody who has stopped, and twelve refused is a client we are failing.
 *
 * `dropped` is deliberately its own number and not folded into either closed
 * one: nobody refused us and nothing came down, so counting it as a win or a
 * loss would make that number a lie.
 */
export interface ClientCaseCounts {
  profileId: string;
  stageName: string;
  accountId: string;
  accountName: string | null;
  accountKind: string | null;
  /** Who holds her — the stand-in while there is one. */
  supportLabel: string | null;
  /** True when that name is a stand-in rather than the regular member. */
  covering: boolean;
  /** Set when this is the reader's own client and somebody else is covering
   *  it: shown so they can see where it went, marked so they know it is not
   *  theirs to touch today. */
  lentTo?: string | null;
  total: number;
  inWork: number;
  onHold: number;
  closedWell: number;
  closedBadly: number;
  dropped: number;
}

/**
 * The support console's board, in one answer: the first page of every column
 * it draws, the true count behind each heading, and what is asking to be
 * looked at first.
 *
 * One shape rather than six requests. The board asked per column plus twice
 * for counts, which meant a screen redrawing itself opened seven connections
 * to a pool this product shares with its own scanners.
 */
export interface AdminCaseBoard {
  /** Keyed by status, in whatever order the screen asked for them. */
  columns: Record<string, Page<DomainCaseSummary>>;
  /** How many cases each status really holds under these filters, not how
   *  many happen to be on the first page. */
  counts: Record<string, number>;
  /** The "Start here" bar: how many cases each reason is true of. */
  attention: Record<string, number>;
}

/**
 * One domain case as the panel sees it, across every client. A case covers
 * many URLs and up to six independent threads, so anything per-attempt —
 * `attempts`, the next action — is on {@link ChannelThreadSummary}, not here.
 */
export interface DomainCaseSummary {
  id: string;
  domain: string;
  /** The case's own number, unique across the product — what support says out
   *  loud. `num` beside it is the round on this domain and repeats. */
  ref: number;
  num: number;
  status: DomainCaseStatus;
  collecting: boolean;
  assigneeId: string | null;
  assigneeLabel: string | null;
  /**
   * The support member the *client* belongs to, which is a different question
   * from who took this case: a case has an assignee only once somebody
   * reached for it. Null when nobody holds the client. The stand-in wins while
   * there is one — today that client is theirs.
   */
  supportLabel: string | null;
  clientLabel: string;
  profileId: string;
  accountId: string;
  openedAt: string;
  resolvedAt: string | null;
  closeReason: string | null;
  holdReason: string | null;
  holdUntil: string | null;
  /** When it was parked, for "12 days on hold" on the board. Null on a case
   *  parked before this was recorded, and on every case that is not on hold. */
  heldAt: string | null;
  urlCount: number;
  removedCount: number;
  /** True when at least one thread needs a human look right now. */
  needsAttention: boolean;
  /** We already filed on this site for this client in an earlier round: a
   *  repeat offender, whose notice says so (DEC-006). */
  repeatSite: boolean;
  threads: ChannelThreadSummary[];
  /** Whether DMCA may be sent yet; see TakedownService.dmcaGateOpen. */
  dmcaGateOpen: boolean;
  /**
   * What kind of site this domain is — `DomainTrustLevel`. Orders the queue
   * (worst first) and tells support how carefully to read before writing.
   * Never null: a domain nobody has judged reads as `suspicious`.
   */
  trustLevel: string;
  /**
   * Whether her own photos back this case up — `PhotoMatchState`, taken from
   * the strongest of its URLs. A tag, never a gate: every notice is sent by a
   * person, so nothing here blocks anything.
   */
  photoMatch: string;
}

export interface DomainCaseDetail extends DomainCaseSummary {
  urls: DomainCaseUrlSummary[];
}

/**
 * The same case as her own screens see it, and deliberately less.
 *
 * `DomainCaseSummary` above is the panel's projection: it carries the abuse
 * address a notice went to, which support member holds the case, why it was
 * parked, why a channel failed, and how much correspondence there has been.
 * None of that is hers — "кому писали і що відповіли клієнтка не бачить, це
 * робота підтримки" — and a screen simply not rendering a field is not the
 * same as not sending it: a client session could read every one of them out
 * of the JSON. So the client routes project into this instead, and a new
 * staff-only field added above cannot leak by being forgotten here.
 */
export interface ClientChannelSummary {
  channel: TakedownChannel;
  /** Her version of the state: no failure reason, no address, no counts. */
  status: ChannelThreadStatus;
  sentAt: string | null;
  resolvedAt: string | null;
}

/** A Help ticket on this case where support is waiting on her: the only
 *  thing a case can ask of the client, and it is asked in the ticket. */
export interface ClientCaseAction {
  ticketId: string;
  ticketRef: number;
  subject: string;
}

export interface ClientCaseSummary {
  id: string;
  /** The case number support reads out; what she searches by. */
  ref: number;
  domain: string;
  num: number;
  /** The protected profile this case is for. */
  profileName: string | null;
  status: DomainCaseStatus;
  openedAt: string;
  resolvedAt: string | null;
  urlCount: number;
  removedCount: number;
  /** Parked, without saying what support is waiting on internally. */
  onHold: boolean;
  channels: ClientChannelSummary[];
  /** The steps this removal really went through, in order (DEC-001). */
  journey: ClientStepAt[];
  /** The latest moment she could see: opened, sent, answered, link added,
   *  delisted, gone, closed. Support's own moves (taking, parking) are not. */
  lastUpdatedAt: string;
  /** Pages of this case on the re-upload schedule. */
  watchingCount: number;
  actionRequired: ClientCaseAction[];
}

export interface ClientCaseDetail extends ClientCaseSummary {
  urls: DomainCaseUrlSummary[];
}

/** One message on a channel thread, in or out. */
export interface CaseMessageRecord {
  id: string;
  channel: TakedownChannel;
  direction: 'in' | 'out';
  fromAddress: string | null;
  subject: string | null;
  body: string;
  classification: string | null;
  createdAt: string;
}

/**
 * One thing that happened to a case, in order. Only ever served from the
 * staff-facing `/admin/takedown/cases/:id/history` today — no client-facing
 * endpoint returns it. `payload` carries whatever that event recorded
 * (a channel, a recipient, a decline reason, an external case id): fine for
 * a support screen, not something to forward verbatim to a client-facing
 * route without the same thought the old client history gave it (event type
 * and actor only, nothing about which address a notice went to).
 */
export interface CaseEventRecord {
  id: string;
  type: string;
  actorLabel: string | null;
  /**
   * Under whose authority, when that is not the actor's own — "standing in
   * for Anna". Null for an action by the client's own support member or by a
   * supervisor, where the actor's name says everything.
   */
  capacityLabel: string | null;
  payload: Record<string, unknown> | null;
  createdAt: string;
}

/** Backward-compat alias kept only for `apps/web`'s simpler client view. */
export type CaseSummary = DomainCaseSummary;
export type CaseDetail = DomainCaseDetail;

/**
 * The board's own two addresses, in one place: both the console and Activity
 * draw the same board, and a path typed twice is a path that drifts once.
 */
export const CASE_BOARD_PATHS = {
  /** The whole board in one request: every column, both sets of counts. */
  board: '/admin/takedown/board',
  cases: '/admin/takedown/cases',
  statusCounts: '/admin/takedown/case-status-counts',
  attentionCounts: '/admin/takedown/attention-counts',
} as const;

/**
 * Display order for the channel ladder. `dmca` last on purpose — see
 * {@link TakedownChannel.DMCA}'s own doc for the gate. Rendered as a rail in
 * both apps, order carries meaning, so it lives here rather than being
 * retyped per app.
 */
export const CHANNEL_LADDER: ReadonlyArray<{ key: TakedownChannel; label: string }> = [
  { key: TakedownChannel.DOMAIN_EMAIL, label: 'Domain email' },
  { key: TakedownChannel.HOSTER_ABUSE, label: 'Hoster' },
  { key: TakedownChannel.REGISTRAR_ABUSE, label: 'Registrar' },
  { key: TakedownChannel.CDN_ABUSE, label: 'CDN' },
  { key: TakedownChannel.SEARCH_DELISTING, label: 'Delisting' },
  { key: TakedownChannel.DMCA, label: 'DMCA' },
];

/**
 * One message a case would produce if it were sent right now.
 *
 * `route` is the part a reviewer needs and a status word would hide: `email`
 * leaves as mail from our own mailbox, `api` is a call to the provider's own
 * abuse endpoint, and `manual` is a form that will sit waiting for a person.
 */
export interface DispatchPreviewMessage {
  route: 'email' | 'api' | 'manual';
  /** Mailbox, or the endpoint the call would hit. */
  to: string;
  /** Who this reaches, in the reviewer's words: 'Hoster', 'Cloudflare', 'bing'. */
  label: string;
  subject: string | null;
  body: string;
  /** Whether support may rewrite `body`/`subject` before sending. False for
   * an API call (dmca.com, a CDN's abuse endpoint) whose shape is fixed. */
  editable: boolean;
  /** Why this one is not what it looks like. Usually null. */
  note: string | null;
}

/**
 * Everything sending one channel of a domain case would actually do, rendered
 * before it happens and editable before it is sent — the draft support opens,
 * edits, and either sends or cancels. One notice lists every URL in the case,
 * not one preview per URL.
 *
 * This exists because approval used to be a decision about an unseen document:
 * the notice was rendered inside the send, so the first reader of a legal
 * statement made in a client's name was the abuse desk receiving it.
 */
export interface DispatchPreview {
  caseId: string;
  channel: TakedownChannel;
  domain: string;
  /** Every URL this notice will list. */
  urls: string[];
  rightsHolder: string;
  /** The mailbox notices leave from, null when SMTP is not configured. */
  from: string | null;
  replyTo: string | null;
  messages: DispatchPreviewMessage[];
  /** A handful of URLs to attach a screenshot for, picked at random — not
   * every URL, see the manager's note on evidence in the case doc. */
  evidenceSampleUrls: string[];
  /** Why this cannot be sent right now. Empty means it can. */
  blockers: string[];
  /**
   * Things a reviewer should read before approving but which do not stop a
   * send. Kept apart from `blockers` on purpose: a delisting is publicly logged
   * and that is worth a warning, not a refusal, and folding the two together
   * would either block a legitimate filing or hide a real one.
   */
  warnings: string[];
}

/**
 * One pass over one profile.
 *
 * `queriesRun` is the field that earns its place: a pass that asked hundreds of
 * questions and found nothing is the product working, and a pass that asked
 * none is an outage. Without it the two look identical from outside.
 */
export interface ScanRun {
  id: string;
  mode: string;
  startedAt: string;
  finishedAt: string | null;
  status: ScanRunStatus;
  queriesRun: number;
  newDetections: number;
  failureReason: string | null;
  profile: { id: string; stageName: string };
  /** Which source is being asked now; null once the run is over. */
  stage: string | null;
  stagesTotal: number;
  stagesDone: number;
}

/**
 * Every run this account has had, counted by the database. Not derived from
 * `/detection/runs`, which returns the latest hundred and would cap the total.
 */
export interface ScanRunSummary {
  total: number;
  complete: number;
  failed: number;
  lastStartedAt: string | null;
}

/**
 * One page of a list, in the shape Stripe uses.
 *
 * No total and no page number, on purpose. Findings are appended by scans all
 * day: a total would be wrong by the time it was read, and page three would
 * point at different rows on every visit. `hasMore` is the honest version of
 * the same question, and the id of the last row is how the next page is asked
 * for.
 */
export interface Page<T> {
  data: T[];
  hasMore: boolean;
}

/** The counts on the overview, which a paged list can no longer supply. */
export interface FindingSummary {
  total: number;
  newToday: number;
  awaiting: number;
  filed: number;
  /** Filed and not finished: still with support, sent, or awaiting the host. */
  pending: number;
  /**
   * Ended with the content gone, by either route. Kept for the places that
   * genuinely need one number (an internal queue length, a health check) and
   * never shown to a client, see the two counts below.
   */
  removed: number;
  /**
   * The file is off the host. The page is gone; there is nothing left to find.
   */
  removedAtSource: number;
  /**
   * The page is out of Google, and still on the internet. Anybody with the
   * address can still open it.
   *
   * Split from the one above because adding them together is the single most
   * common dishonesty in this industry: a vendor reports "1,247 removed" where
   * most of that number is search results hidden from a page that is still up.
   * The client cannot tell the difference from the total, and the difference is
   * the whole question of whether her material is still online.
   */
  delisted: number;
  /** Ended without it: refused, unsendable, or put back up. */
  refused: number;
  /** Never compared against the customer's own images. Not a weak match. */
  unchecked: number;
  repeats: number;
  /** The hosts this account has been found on, commonest first. */
  domains: { domain: string; count: number }[];
  /**
   * How many of those hosts Google has already been asked to clear pages from.
   *
   * Present so that "no findings on recorded sites" can be told apart from "no
   * record has been imported". The figure comes from Google's published
   * removals dump, which a person loads by running a script; until they have,
   * this is zero for everybody and the filter that reads it would return an
   * empty list on a full report, a silent wrong answer of exactly the kind
   * this product exists to avoid giving.
   */
  knownSites: number;
  /**
   * What the overview's revenue-at-risk estimate is worked out from: her
   * detections split by how sure the match is and whether the page is still up.
   * Counted here rather than on the page, which holds ten rows of however many
   * hundred and would put the estimate of a large account somewhere near zero.
   */
  exposure: ExposureCounts;
}


/**
 * One of the client's own images, as the reference library lists it.
 *
 * `matches` is the number that makes the library worth opening: a list of files
 * with no consequences attached is a file manager. `state` has three values
 * because the middle one matters -- an image with no fingerprint yet is not
 * protecting anything, however long it has been sitting in the list.
 */
export interface ReferenceAsset {
  id: string;
  label: string;
  mimeType: string | null;
  matches: number;
  state: 'protected' | 'processing' | 'archived';
  createdAt: string;
}

/**
 * The operation in counts, for the first screen of the day.
 *
 * Ordered by what blocks what: an unchecked letter stops anything being filed
 * for that client, an unreviewed case stops that notice going out, a stuck one
 * has nowhere to go, and an overdue follow-up means a host is being left alone.
 * Everything after `inFlight` is context rather than work.
 */
/**
 * One person's day, as the panel can honestly describe it.
 *
 * Two clocks, never added together: `presentMinutes` is beaten out by the
 * panel while its tab is open and is a fact; `activeMinutes` is worked out
 * from the gaps between what they did and is an estimate — somebody reading a
 * long correspondence is present and does nothing for twenty minutes.
 */
export interface StaffDayPerson {
  userId: string;
  name: string;
  email: string;
  role: string;
  firstAt: string | null;
  lastAt: string | null;
  presentMinutes: number;
  activeMinutes: number;
  /** How many things they did, by our own vocabulary: `case.sent`, `note.written`. */
  did: Record<string, number>;
  actions: number;
}

/** One thing somebody did, for the day's feed. */
export interface StaffDayEvent {
  userId: string;
  at: string;
  kind: string;
  what: string;
  /** Where to open what it happened to, or null where there is nothing. */
  href: string | null;
}

export interface StaffDay {
  /** The day this is, as `YYYY-MM-DD` in the panel's own timezone. */
  day: string;
  people: StaffDayPerson[];
  feed: StaffDayEvent[];
}

export interface OpsOverview {
  lettersWaiting: number;
  needsReview: number;
  stuck: number;
  overdue: number;
  disputed: number;
  inFlight: number;
  /** Kept apart, for the same reason they are apart on the client's overview. */
  removedAtSource: number;
  delisted: number;
  refused: number;
  clients: number;
  signupsWeek: number;
  profilesWatched: number;
  /** Signed up and unable to have anything filed. The activation gap, stated. */
  profilesUnverified: number;
  findingsNew: number;
  scansDay: number;
  scansFailedDay: number;
  hostsKnown: number;
  hostsBlocking: number;
  /** Cases the system closed by itself in the last day: every link confirmed
   *  gone, which is the one call it is allowed to make. */
  closedBySystem: number;
}

/** Overview's own trend — a window wide enough that a single bad day reads
 *  as a data point, not as a crisis. See OverviewService.trend. */
export interface OpsOverviewTrend {
  days: number;
  scans: Array<{ day: string; total: number; failed: number }>;
  closed: Array<{ day: string; total: number; bySystem: number }>;
}

/**
 * One account's real search spend, on the /spend admin page.
 *
 * "This cycle" is since `billingPeriodStartedAt` for a paying account, or the
 * trailing 30 days for a free one, which has no billing cycle to measure
 * from. Only accounts that have actually spent something appear at all.
 */
export interface AccountSpendRow {
  accountId: string;
  name: string;
  planCode: PlanCode | null;
  paying: boolean;
  spendThisCycleUsd: number;
  scansThisCycle: number;
  lastDepth: number | null;
  lastReason: string | null;
  /** Only set for a paying account with a known billing-period start. */
  daysUntilRenewal: number | null;
  /** Real trailing-7-day average, $/day, a pace, not a prediction. Same figure the detail modal shows. */
  dailyRunRateUsd: number;
  /** dailyRunRateUsd projected forward, so the whole account list can be scanned for who gets expensive. */
  next30DaysUsd: number;
  /** Soonest of this account's own dated events: a full sweep coming due, or deep search resuming before
   * renewal. Null when neither is known yet. The full list of what's coming is in the detail modal. */
  nextEvent: AccountSpendForecastEvent | null;
  /** How many profiles this account actually protects right now: dailyRunRateUsd ÷ this is a real,
   * live $/profile/day, which is what a per-plan cost-at-scale projection should scale by the plan's
   * entitled profile count, rather than by a headcount of accounts using however many profiles they
   * each happen to today. */
  profileCount: number;
}

/** What `/admin/search-spend/summary` answers: today's total, the current depth tiers, and who spent what. */
export interface SearchSpendSummary {
  today: { spentUsd: number; budgetUsd: number };
  depth: { firstScanResults: number; steadyResults: number; renewalWindowDays: number };
  accounts: AccountSpendRow[];
  /**
   * What each depth-tier reason has actually cost, averaged across every
   * `SearchSpendEvent` logged under it, not a formula, a measurement, so
   * "why does a renewal week cost more" has a real number behind it rather
   * than an estimate of how many site-chain queries one sweep issues.
   * Sorted priciest first; a reason nothing has been logged under yet is
   * simply absent.
   */
  pricingByReason: Array<{ reason: DepthReason; avgUsd: number; scans: number }>;
  /**
   * What a fully-used client on each paying plan actually costs to search,
   * computed at request time from the real connector query-builders and the
   * real price meter (`searchRequestCost`), the same functions that price a
   * live scan, run here against the queries a scan would issue rather than
   * ones it did. Never guessed, never sampled from whatever accounts happen
   * to exist: it needs no real account to compute, and updates the moment a
   * site list, a term list, a price or a depth setting does.
   */
  planDailyCost: PlanDailyCost[];
  /**
   * The per-account daily search ceiling (Service keys → Search), in dollars.
   * Nothing any account spends on paid search can exceed this now; a
   * per-plan "at full use" figure above it is a number that used to be
   * real and no longer can happen, not a real ceiling.
   */
  accountDailyCeilingUsd: number;
}

/**
 * One paying plan's real daily cost, blended across a 30-day cycle by the
 * real renewalWindowDays setting: mostly steadyUsd, a `renewalWindowDays`
 * slice of fullSweepUsd. See SearchSpendSummary.planDailyCost.
 */
export interface PlanDailyCost {
  planCode: PlanCode;
  /** $/day on the common day: shallow depth, no site-chain sweep. */
  steadyUsd: number;
  /** $/day on a first scan or a day inside the renewal window: full depth, every site-chain sweep the plan's sources buy. */
  fullSweepUsd: number;
  /** The number to actually plan around: steadyUsd most days, fullSweepUsd on renewalWindowDays of the cycle. */
  blendedDailyUsd: number;
}

/** An outside provider the business is billed by, one row of the spend split. */
export type EconomicsSpendSource = 'search' | 'whois';

/** One source's share of provider spend over the period, with a human label. */
export interface EconomicsSpendRow {
  source: EconomicsSpendSource;
  label: string;
  spendUsd: number;
}

/** One calendar month of the revenue-vs-cost series the chart draws. */
export interface EconomicsMonth {
  /** YYYY-MM, UTC. */
  month: string;
  revenueUsd: number;
  providerSpendUsd: number;
  /** (revenue - spend) / revenue, as a percent; null for a month with no revenue. */
  grossMarginPct: number | null;
}

/**
 * What `/admin/economics` answers: what the business paid outside providers
 * against what it earned over the same window, and the ratios that fall out of
 * the two. Revenue is net of refunds and disputes; provider spend is the real
 * SearchSpendEvent log plus billed WHOIS lookups, never an estimate of search.
 */
export interface Economics {
  /** The window every figure covers, inclusive, as YYYY-MM-DD (UTC). */
  periodStart: string;
  periodEnd: string;
  revenueUsd: number;
  providerSpendUsd: number;
  spendBySource: EconomicsSpendRow[];
  /** (revenue - providerSpend) / revenue, as a percent; null when no revenue. */
  grossMarginPct: number | null;
  /** providerSpend / takedown cases opened in the window; null when none opened. */
  costPerTakedownUsd: number | null;
  /** providerSpend / accounts; null when there are none. */
  costPerClientUsd: number | null;
  /** DomainCase cases opened in the window, the cost-per-takedown denominator. */
  takedowns: number;
  /** Accounts on record, the cost-per-client denominator. */
  clients: number;
  months: EconomicsMonth[];
  /** Calendar months the window covers, the one the reader picked. */
  rangeMonths: number;
  /** What the figures were narrowed to, or null when they cover every account. Money that no account owns (billed WHOIS) is left out of a narrowed view. */
  filter: { plan: string | null; kind: string | null } | null;
  /** The same number of months straight before this window, for "against what". Null when it holds nothing to compare. */
  previous: EconomicsPrevious | null;
  /** Revenue and provider cost grouped by the plan each account is on. */
  byPlan: EconomicsPlanRow[];
  /** The accounts that cost the most to serve in the window, costliest first. */
  topAccounts: EconomicsAccountRow[];
  /** Accounts whose provider cost in the window was more than they paid. */
  losing: { accounts: number; spendUsd: number; revenueUsd: number };
}

/** The week-based periods the screens offer (signups, takedown health), in weeks. */
export const RANGE_WEEKS: readonly number[] = [4, 12, 26];

/** The windows the money screens offer, in calendar months. One list, so the screens and the API agree. */
export const FINANCE_RANGE_MONTHS: readonly number[] = [3, 6, 12];

/** The window before the one shown, whole-window figures only. */
export interface EconomicsPrevious {
  periodStart: string;
  periodEnd: string;
  revenueUsd: number;
  providerSpendUsd: number;
  grossMarginPct: number | null;
}

/** One plan's slice. A null `planCode` is the accounts with no subscription. */
export interface EconomicsPlanRow {
  planCode: string | null;
  accounts: number;
  revenueUsd: number;
  spendUsd: number;
  grossMarginPct: number | null;
}

/** One account's revenue against what serving it cost. */
export interface EconomicsAccountRow {
  accountId: string;
  /** "CL-2041", the number support says out loud. */
  ref: string;
  name: string | null;
  planCode: string | null;
  revenueUsd: number;
  spendUsd: number;
  grossMarginPct: number | null;
}

/**
 * The three reasons a scan searched at whichever depth it did, written by
 * `DetectionService.effectiveDepth()`, the only place that decides one, and
 * read everywhere else a `SearchSpendEvent.reason` is grouped, matched or
 * displayed: the dev seed script's fixtures, the pricing-by-reason average,
 * `/spend`'s own depth-and-price card. A string typed out separately at each
 * of those would still work until the day one of them was typed differently.
 */

/** One day of one account's real spend. A day with no `SearchSpendEvent` is simply absent, not a zero row. */
export interface AccountSpendDay {
  /** YYYY-MM-DD, UTC. */
  date: string;
  costUsd: number;
  scans: number;
  /** costUsd split by depth-tier reason; see {@link DepthReason}. */
  byReason: Partial<Record<SpendReason, number>>;
  /** How many of the day's scans fell under each reason, same keys as byReason. */
  scansByReason: Partial<Record<SpendReason, number>>;
}

/**
 * A future date something gets pricier, not a future price. See
 * `AccountSpendDetail.forecast`: whether it actually happens (a manager
 * pressing Scan, the free crawl finding a new address) is never guessed at,
 * only what's already certain from the schedule.
 */
export interface AccountSpendForecastEvent {
  date: string;
  kind: 'chain-due' | 'renewal-window';
  note: string;
}

/** What `/admin/search-spend/accounts/:id` answers, for the detail opened from one row of the summary table. */
export interface AccountSpendDetail {
  accountId: string;
  name: string;
  planCode: PlanCode | null;
  paying: boolean;
  /** Last 30 days, most recent first. */
  daily: AccountSpendDay[];
  forecast: {
    /** Real trailing-7-day average, $/day, a pace, not a prediction. */
    dailyRunRateUsd: number;
    next7DaysUsd: number;
    next30DaysUsd: number;
    /** Sorted soonest first, next 30 days only. */
    upcomingEvents: AccountSpendForecastEvent[];
  };
}

/* ── an account that is a company ──────────────────────────────────────────
   An agency is an account with more than one profile and more than one person
   in it. Both of those already existed; these are the shapes that name it.

   `AccountKind` and `AccountRole` come from ./enums, beside the sets and
   labels built from them. This file wrote them out again as unions until
   2026-09-08 — two definitions of the same three words, and only one of them
   checked against the schema. */

export interface AccountDetail {
  id: string;
  kind: AccountKind;
  /** The agency's name. Null on a solo account, where the profile carries it. */
  name: string | null;
  /** Null without a subscription. */
  planCode: PlanCode | null;
  createdAt: string;
  _count: { profiles: number; users: number };
  /**
   * True once the account has a detection or a finished scan of its own.
   *
   * The one answer to "is this account still being shown samples" — see
   * `useSampleMode` in packages/ui. It used to be five answers, one per screen,
   * read off five different lists.
   */
  started: boolean;
  /** The signed-in person's own role here, for screens that hide what it cannot do. */
  myRole: AccountRole;
  /**
   * A fake account for walking the product; the client app shows a "Scan now"
   * trigger on it (real accounts scan on a schedule, no button) so a manager can
   * run the simulator and watch the result. See Account.isDemo.
   */
  isDemo: boolean;
}

/**
 * The frame every screen in the client app is drawn inside, in one answer.
 *
 * The rail asked for the plan, the account, the unread count and the whole
 * profile list on its own — the last two on every navigation — so a person
 * moving between screens paid four requests before the screen they wanted had
 * asked for anything. `needsPersonalData` is the one thing that list was for.
 */
export interface PanelShell {
  entitlements: Entitlements;
  /** An active subscription right now: what paid-only actions such as Report a Page open for. */
  paying: boolean;
  account: AccountDetail;
  /** Notifications not yet read, for the badge on the rail. */
  unread: number;
  /** Things waiting on her to act, from the account's state, not from what is unread. */
  actions: number;
  /**
   * Whether the account has yet said who it is protecting: at least one handle
   * and one page that is genuinely theirs. Everything else on a profile is
   * optional; a search with neither is not a search.
   */
  needsPersonalData: boolean;
  /** Whether the referral programme is on, so the rail shows its page. */
  referrals: boolean;
}

export interface AccountMember {
  id: string;
  email: string;
  name: string | null;
  accountRole: AccountRole;
  createdAt: string;
  disabledAt: string | null;
}

/** An offer to join, still standing: not taken up, not withdrawn, not expired. */
export interface AccountInvitation {
  id: string;
  email: string;
  role: AccountRole;
  expiresAt: string;
  createdAt: string;
}

/**
 * One model in an agency's portfolio.
 *
 * `removedAtSource` and `delisted` stay apart here as they do everywhere else.
 * An agency defending a renewal is the reader most tempted by one big number,
 * and the one for whom being caught inflating it is most expensive.
 */
/** Where an agency's invitation of a model into her own SOLO cabinet stands. */
export type CreatorInviteState = 'not_invited' | 'pending' | 'confirmed';

export interface CreatorInviteStatus {
  state: CreatorInviteState;
  /** Address the invitation was (or will be) sent to. Empty when never invited. */
  email: string;
}

/** Where the OnlyFans bio-notice check stands, for the Creators checklist. */
export type OwnershipBioState = 'verified' | 'missing' | 'lost' | 'never';

export interface PortfolioEntry {
  id: string;
  stageName: string;
  /** Which colleague looks after her. Null means nobody has claimed it. */
  managerId: string | null;
  authorizationStatus: AuthorizationStatus;
  /** Bio-notice check on her OnlyFans page; always set on agency portfolio rows. */
  ownershipBio: OwnershipBioState;
  lastScanAt: string | null;
  /** When watching began. Null means this profile has never been searched for. */
  watchedSince: string | null;
  findings: number;
  /** Findings whose page the liveness check last found still up. */
  stillLive: number;
  openCases: number;
  removedAtSource: number;
  delisted: number;
  protection: ProtectionState;
  /** What the agency can act on for her, highest priority first; empty when nothing. */
  attention: CreatorAttention[];
  /** Invitation into her own Snily cabinet; always present for agency portfolio rows. */
  creatorInvite: CreatorInviteStatus;
  /**
   * Paid agency: invite + bio + letter. Null when the checklist does not
   * apply (solo account, or unpaid free-scan path).
   */
  setupComplete: boolean | null;
}

/**
 * One Stripe webhook event, on the admin Payments page: every attempt
 * Stripe told us about, successful or not, in real time and across all
 * history. `id` is the webhook event's own id (evt_...); `objectId` is the
 * underlying thing that happened (a checkout session, subscription, invoice,
 * or dispute id), the one worth pasting into the Stripe dashboard's search.
 */
export interface BillingEventRow {
  id: string;
  type: string;
  outcome: string | null;
  objectId: string | null;
  accountId: string | null;
  /** Account name, or the first user's email, or accountId, whichever the account actually has. Null when unmatched. */
  accountLabel: string | null;
  amountUsd: number | null;
  processedAt: string;
}

/**
 * One real HTTP call to DataForSEO, on the admin Search requests page: the
 * raw method/path/status/latency, for the list; the full request and
 * response bodies are a separate fetch (`SearchRequestLogDetail`), same
 * split as `BillingEventRow`/`eventPayload`, so a page of five hundred rows
 * isn't also five hundred SERP payloads.
 */
export interface SearchRequestLogRow {
  id: string;
  createdAt: string;
  accountId: string | null;
  accountLabel: string | null;
  profileId: string | null;
  mode: SearchMode | null;
  reason: DepthReason | null;
  /** The caller's own key for this call; `search-compare` joins by it. */
  token: string | null;
  method: string;
  path: string;
  sandbox: boolean;
  responseStatus: number | null;
  errorMessage: string | null;
  costUsd: number;
  durationMs: number | null;
}

/** The "View" button's payload for one `SearchRequestLogRow`: exactly what was sent and what came back. */
export interface SearchRequestLogDetail {
  requestBody: unknown;
  responseBody: unknown;
}

/**
 * One scan, on the admin Scans page: every sweep this product has actually
 * run, not just the cost `/spend` derives from the same rows. "How deep, how
 * often, for which clients" has no other answer today short of reading the
 * database directly.
 */
export interface ScanRunRow {
  id: string;
  profileId: string;
  profileName: string;
  accountId: string;
  /** Account name, or the first user's email, or accountId, whichever the account actually has. */
  accountLabel: string;
  /** Null on a row whose stored word this build has never had. */
  mode: ScanMode | null;
  /** A row stuck at `running` is a scan that died. */
  status: ScanRunStatus;
  startedAt: string;
  finishedAt: string | null;
  /** Null on runs from before this was recorded: read as "unknown", never as "no". */
  boughtChains: boolean | null;
  queriesRun: number;
  newDetections: number;
  /** Which source is being asked right now; null once the run is over. */
  stage: string | null;
  stagesTotal: number;
  stagesDone: number;
  failureReason: string | null;
}

/**
 * One message this product told a client, on the admin Notifications page.
 * Support used to find out a client was never told something the same way a
 * client did, by being asked. `sendError` set means the email itself never
 * left; a client cannot have missed something that was never sent.
 */
export interface NotificationLogRow {
  id: string;
  accountId: string;
  accountLabel: string;
  /** finding | weekly_digest | case_resolved | authorization | quota */
  kind: string;
  title: string;
  createdAt: string;
  /** Null while the email has not gone out yet. */
  sentAt: string | null;
  sendError: string | null;
  readAt: string | null;
}

/**
 * One open conversation, on the admin Support inbox. `claimedById` is only
 * ever set while the claim is fresh: a stale claim (nobody replied in 15
 * minutes) reads as unclaimed again, same as the backend enforces it.
 */
export interface SupportInboxRow {
  id: string;
  /** Which account this conversation belongs to — so `/support?accountId=` can filter. */
  accountId: string;
  channel: SupportChannel;
  client: string;
  plan: string;
  lastMessage: string | null;
  lastMessageAt: string;
  waitingMinutes: number;
  claimedById: string | null;
  claimedAt: string | null;
  /** Set when the conversation is a ticket filed from Help. */
  ticket: SupportTicketBrief | null;
}

/** What support sees of a ticket beside its conversation. */
export interface SupportTicketBrief {
  id: string;
  ref: number;
  subject: string;
  requestType: SupportRequestType;
  status: SupportTicketStatus;
  urgent: boolean;
  /** "Case #123 · example.com", or a reported page's address. */
  related: string | null;
  /** Where `related` opens: the case on the board, or the reported page itself. */
  caseId: string | null;
  reportUrl: string | null;
  createdAt: string;
  lastActivityAt: string;
  /** Who on the account filed it; only the opened conversation reads it. */
  filedBy?: string | null;
}

/** A case or a self-reported page a ticket may be about. */
export interface TicketLinkable {
  id: string;
  /** "Case #123", or the page's address for a report. */
  label: string;
  domain: string;
  status: string;
  createdAt: string;
}

/** One row of My Tickets. */
export interface TicketRow {
  id: string;
  ref: number;
  subject: string;
  requestType: SupportRequestType;
  status: SupportTicketStatus;
  urgent: boolean;
  related: TicketLinkable | null;
  createdAt: string;
  lastActivityAt: string;
  /** Who wrote last on the list; absent wherever the row is not from that list. */
  lastReplyFrom?: 'client' | 'operator' | null;
}

export interface TicketPage {
  rows: TicketRow[];
  total: number;
  /** How many of hers are waiting on her right now, whatever the filter. */
  waitingForYou: number;
  /** Not yet resolved or closed, whatever the filter. */
  unresolved: number;
}

export interface TicketMessage {
  id: string;
  author: 'client' | 'operator';
  body: string;
  createdAt: string;
}

export interface TicketEvent {
  id: string;
  kind: 'created' | 'status' | 'support_reply' | 'client_reply';
  status: SupportTicketStatus | null;
  createdAt: string;
}

export interface TicketDetail extends TicketRow {
  description: string;
  messages: TicketMessage[];
  events: TicketEvent[];
}

/** Whether this account may open WhatsApp, and where to. */
export interface WhatsAppLink {
  url: string;
}

export interface SupportThreadMessage {
  id: string;
  /** client | operator | system */
  author: string;
  authorId: string | null;
  body: string;
  createdAt: string;
}

/**
 * How many domains the atlas actually watches right now, read straight off
 * the six connector site lists rather than typed by hand anywhere: see
 * apps/admin/app/(panel)/map/page.tsx, which used to state this as a fixed
 * number in prose and went stale the first time a list changed under it.
 */
export interface AtlasFacts {
  totalDomains: number;
  byCategory: Record<string, number>;
}

/** A thread's full history, for the reply modal. */
export interface SupportThreadDetail {
  id: string;
  channel: SupportChannel;
  status: SupportThreadStatus;
  claimedById: string | null;
  messages: SupportThreadMessage[];
  ticket: SupportTicketBrief | null;
  account: {
    id: string;
    planCode: PlanCode | null;
    billingStatus: string;
    users: Array<{ email: string; name: string | null }>;
  };
  /**
   * What is true of this client right now, beside her message: the three
   * facts every reply starts with, so answering does not begin with opening
   * two other screens.
   */
  context: {
    accountRef: string;
    models: number;
    openCases: number;
    foundThisWeek: number;
    /** The account's own support member: who the client writes to about the
     *  contract or an escalation. Null until a subscription has activated. */
    anchor: string | null;
    /** Everyone of ours on this account — usually one name, two while
     *  somebody is standing in, more on an agency whose cases are shared. */
    support: string[];
  };
}

/**
 * One invitation to join an account's team, on the admin Team invites page:
 * across every account, not one at a time. `status` is computed once here
 * rather than left as three nullable dates for every reader to re-derive.
 */
export interface AccountInviteRow {
  id: string;
  accountId: string;
  accountLabel: string;
  email: string;
  role: string;
  invitedByEmail: string | null;
  createdAt: string;
  expiresAt: string;
  status: InviteStatus;
}

/**
 * One uploaded reference photo, on the admin Reference library page. `phash`
 * null with the asset older than an hour and not archived is the same
 * "stuck" question ops-report.service.ts already asks of the fingerprint
 * queue: a photo findings keep matching against every OTHER reference for
 * because its own fingerprint never arrived.
 */
export interface ReferenceAssetRow {
  id: string;
  profileId: string;
  profileName: string;
  accountId: string;
  accountLabel: string;
  label: string | null;
  originalName: string | null;
  sizeBytes: number | null;
  mimeType: string | null;
  isPublicAvatar: boolean;
  hasPhash: boolean;
  hasEmbedding: boolean;
  matchCount: number;
  createdAt: string;
  archivedAt: string | null;
}

/**
 * One account's usage against a metered quota this billing period, on the
 * admin Usage quotas page: support's answer to "why can't this client file
 * a takedown" without asking them to read a plan table. `limit` null means
 * the plan has no cap on this metric.
 */
export interface UsageQuotaRow {
  accountId: string;
  accountLabel: string;
  planCode: PlanCode | null;
  metric: string;
  periodStart: string;
  periodEnd: string;
  used: number;
  limit: number | null;
}

/**
 * One case event or message, on the admin Case activity page: every case,
 * merged into one feed rather than one at a time behind `/cases`'s own
 * per-case History drill-down. Same shape that drill-down already uses
 * (see case-history.tsx), with the case's client and domain attached so a
 * row means something without opening it first.
 */
export interface CaseActivityRow {
  id: string;
  at: string;
  kind: 'event' | 'message';
  caseId: string;
  accountId: string;
  accountLabel: string;
  domain: string;
  /** Set when kind is 'event'. */
  type: string | null;
  /** Who did it — resolved from the event's own `actorId`. Null for a
   * system-triggered event (a scan joining a URL, an auto-timeout) and for
   * every message: a message's actor is the adjacent 'sent'/'reply' event,
   * not the message row itself. */
  actorLabel: string | null;
  /** Set when kind is 'message'. */
  direction: string | null;
  fromAddress: string | null;
  preview: string | null;
}

/* ── the search-comparison contract ────────────────────────────────────────
   One definition of what /admin/search-compare answers with.

   These were declared twice: nine shapes in the admin screen and three of the
   same names in the API that builds them. Two of the three had not drifted
   yet; `PlanEstimate` had — the server sends `byPattern` and the screen's copy
   did not know the field existed, so nothing could read it and nothing said
   why. */

/** One strategy the generator knows about, as `/strategies` lists them. */
export interface StrategyListItem {
  key: string;
  family: string;
  termCount: number;
  experimental: boolean;
}

/** One query family, with what it cost and what it found. */
export interface QueryBreakdownEntry {
  queryFamilyId: string;
  channel: 'open' | 'site';
  strategy: string;
  generatedByStrategies: string[];
  pattern: string;
  identityForm: 'quoted' | 'bare';
  keyword: string | null;
  keyword2: string | null;
  platform: string | null;
  site: string | null;
  query: string;
  hitsCount: number;
  uniqueHitsCount: number;
  newHitsCount: number;
  duplicateHitsCount: number;
  costMicroUsd: number;
  durationMs: number | null;
}

/** A finished run broken down by strategy. */
export interface StrategyBreakdownRow {
  strategy: string;
  queries: number;
  openUniqueCount: number;
  siteOnlyUniqueCount: number;
  unionUniqueCount: number;
  newUrlsCount: number;
  costMicroUsd: number;
  costPerNewUrlMicroUsd: number | null;
  discoveryEfficiency: number;
}

/** The same run broken down by target site. */
export interface SiteBreakdownRow {
  site: string;
  queries: number;
  hits: number;
  uniqueCount: number;
  alreadyFoundByOpenCount: number;
  siteOnlyCount: number;
  incrementalPct: number;
  costMicroUsd: number;
}

/** And by query family, which is the one the screen does not draw yet. */
export interface QueryFamilyBreakdownRow {
  queryFamilyId: string;
  openUniqueCount: number;
  siteUniqueCount: number;
  overlapCount: number;
  onlyOpenCount: number;
  onlySiteCount: number;
  targetedIncrementalPct: number;
}

/** A URL only the targeted half found: the reason that half exists. */
export interface OnlySiteHit {
  url: string;
  site: string;
  queryFamilyId: string;
  query: string;
  strategy: string;
}

/** One execution row, as history's `detail()` returns them. */
export interface SearchQueryExecutionRow {
  id: string;
  queryFamilyId: string;
  channel: 'open' | 'site';
  strategy: string;
  generatedByStrategies: string[];
  pattern: string;
  identityForm: 'quoted' | 'bare';
  keyword: string | null;
  keyword2: string | null;
  platform: string | null;
  site: string | null;
  query: string;
  hitsCount: number;
  uniqueHitsCount: number;
  newHitsCount: number;
  duplicateHitsCount: number;
  costMicroUsd: number;
  durationMs: number | null;
}

/** What a run that has just finished answers with. */
export interface CompareResult {
  id: string;
  createdAt: string;
  sandbox: boolean;
  depth: number;
  generatedQueryCount: number;
  dedupedQueryCount: number;
  openQueryCount: number;
  siteQueryCount: number;
  openEligibleUniqueCount: number;
  siteEligibleUniqueCount: number;
  unionEligibleUniqueCount: number;
  overlapCount: number;
  onlyOpenCount: number;
  onlySiteCount: number;
  openCoveragePct: number;
  targetedIncrementalCoveragePct: number;
  costOpenMicroUsd: number;
  costSiteMicroUsd: number;
  costAdvancedMicroUsd: number;
  totalCostMicroUsd: number;
  onlySiteHits: OnlySiteHit[];
  byStrategy: StrategyBreakdownRow[];
  bySite: SiteBreakdownRow[];
  queryBreakdown: QueryBreakdownEntry[];
}

/** One row of the history list. */
export interface CompareRunSummary {
  id: string;
  createdAt: string;
  name: string;
  selectedStrategies: string[];
  depth: number | null;
  dedupedQueryCount: number;
  openEligibleUniqueCount: number;
  siteEligibleUniqueCount: number;
  onlySiteCount: number;
  openCoveragePct: number;
  targetedIncrementalCoveragePct: number;
  totalCostMicroUsd: number;
}

/** One run read back in full, with every execution behind it. */
export interface CompareRunDetail {
  id: string;
  createdAt: string;
  name: string;
  usernames: string[];
  aliases: string[];
  whitelist: string[];
  platform: string | null;
  selectedStrategies: string[];
  depth: number | null;
  targetSites: TargetSearchScope[];
  generatedQueryCount: number;
  dedupedQueryCount: number;
  openQueryCount: number;
  siteQueryCount: number;
  openEligibleUniqueCount: number;
  siteEligibleUniqueCount: number;
  unionEligibleUniqueCount: number;
  overlapCount: number;
  onlyOpenCount: number;
  onlySiteCount: number;
  openCoveragePct: number;
  targetedIncrementalCoveragePct: number;
  costOpenMicroUsd: number;
  costSiteMicroUsd: number;
  costAdvancedMicroUsd: number;
  totalCostMicroUsd: number;
  onlySiteHits: OnlySiteHit[];
  executions: SearchQueryExecutionRow[];
  byStrategy: StrategyBreakdownRow[];
  bySite: SiteBreakdownRow[];
}

/** What a plan costs before anybody spends it. */
export interface PlanEstimate {
  generatedQueryCount: number;
  dedupedQueryCount: number;
  openQueryCount: number;
  siteQueryCount: number;
  byStrategy: Record<string, number>;
  byPattern: Record<string, number>;
  maxQueries: number;
  overLimit: boolean;
  /** Priced exactly like a real request, by the formula the spend interceptor
   * bills with, rather than a looser second one that could read differently
   * from the actual bill. */
  depth: number;
  costPerOpenQueryMicroUsd: number;
  costPerSiteQueryMicroUsd: number;
  estimatedCostOpenMicroUsd: number;
  estimatedCostSiteMicroUsd: number;
  estimatedCostTotalMicroUsd: number;
}

/* ── the settings and diagnostics contract ─────────────────────────────────
   Declared twice until 2026-09-08: `ServiceCheck` in the API that builds it
   and again in the panel that draws it, and the two had drifted — the server
   sends `configured` and the panel's copy did not know the field existed. */

/** Whether a probe reached the service at all. */
export type Reach = 'ok' | 'failed' | 'unconfigured' | 'not-probed';

/** One external service, as the diagnostics strip shows it. */
export interface ServiceCheck {
  key: string;
  label: string;
  /** A key is present, whether or not a probe has run. */
  configured: boolean;
  reachable: Reach;
  /** The verdict in a word or two: "Authenticated", "$29.93 balance". */
  headline: string | null;
  /** Its particulars: which mailbox, which bucket, which number. Split here
   *  rather than in the panel, which would have to guess where a sentence ends
   *  and would guess wrong the first time a provider worded it differently. */
  detail: string | null;
  /** Named checks shown as their own cells beside the verdict: SPF, DKIM. */
  checks?: ProbeCheck[];
  /** What the probe read, shown on request rather than by default. */
  records?: ProbeRecord[];
  /** When the check ran (unix ms), so the panel can say how fresh it is. */
  checkedAt: number;
}

/** One row of the settings catalogue. */
export interface SettingRow {
  key: string;
  label: string;
  group: string;
  hint: string;
  isSecret: boolean;
  isSet: boolean;
  unreadable: boolean;
  preview: string | null;
  /** A sample valid value, shown as the input placeholder. */
  example: string;
  /**
   * What happens when this one is left empty, where empty is a decision. Null
   * for most of the catalogue, where unset is a hole; set for the handful with
   * a defined behaviour. The panel counted those into the same red badge as a
   * missing mail password until this existed.
   */
  defaultsTo: string | null;
  /** Present only on a bounded numeric knob: a depth, a budget, a count of days. */
  slider: { min: number; max: number; step?: number } | null;
  /** True only where the whole value space is "true" or "false". */
  boolean: boolean;
  /** A closed word list rendered as a dropdown; null for a free text field. */
  options: readonly string[] | null;
  /** Why the setting exists, for the explain column; null where not written yet. */
  purpose: string | null;
  /** Where the value is used, for the explain column; null where not written yet. */
  usedIn: string | null;
}

/** One line of who changed which setting. */
export interface AuditEntry {
  id: string;
  key: string;
  action: string;
  actorEmail: string | null;
  createdAt: string;
  /** Only present for a developer; the API leaves them out for everyone else. */
  previousValue?: string | null;
  newValue?: string | null;
}

/* ── referrals ───────────────────────────────────────────────────── */

/** One person a client invited, as she sees them on her own page. */
export interface ClientReferralRow {
  id: string;
  /** Masked: a friend is recognisable, the address is not handed out. */
  who: string;
  status: ReferralStatus;
  joinedAt: string;
  firstPaidAt: string | null;
  /** Everything this invitation has earned so far, credited or still holding. */
  earnedCents: number;
}

/** The client's referral page: her link, the terms today, what it has earned. */
export interface ClientReferralSummary {
  enabled: boolean;
  /** The full invite link, or null while the programme is off. */
  link: string | null;
  code: string | null;
  terms: ReferralTerms;
  invited: number;
  paying: number;
  /** Earned and waiting out the refund window. */
  holdingCents: number;
  /** Already on her balance, spent or not. */
  creditedCents: number;
  rows: ClientReferralRow[];
}

/** One invitation, as the admin Referrals screen lists it. */
export interface AdminReferralRow {
  id: string;
  referrerAccountId: string;
  /** "CL-2041": the number a person says out loud. */
  referrerRef: string;
  /** The agency's name, or the address of the person on a solo account. */
  referrerWho: string;
  referredAccountId: string;
  referredRef: string;
  referredWho: string;
  code: string;
  status: ReferralStatus;
  suspicion: string | null;
  blockedReason: string | null;
  createdAt: string;
  firstPaidAt: string | null;
  holdingCents: number;
  creditedCents: number;
  reversedCents: number;
}

/** One reward — a share of one paid invoice — as the admin screen lists it. */
export interface AdminReferralRewardRow {
  id: string;
  referralId: string;
  referrerAccountId: string;
  referrerRef: string;
  referrerWho: string;
  referredAccountId: string;
  referredRef: string;
  referredWho: string;
  invoiceId: string;
  paidCents: number;
  percent: number;
  rewardCents: number;
  status: ReferralRewardStatus;
  availableAt: string;
  creditedAt: string | null;
  reversedAt: string | null;
  reversedReason: string | null;
  createdAt: string;
}

/** Everything the admin Referrals screen draws, in one answer. */
export interface AdminReferralsOverview {
  enabled: boolean;
  terms: ReferralTerms;
  referrals: AdminReferralRow[];
  rewards: AdminReferralRewardRow[];
}

/** One page on a site, as the client's live search screen shows it. */
export interface LiveLink {
  id: string;
  url: string;
  /** Still up, as far as the last check knows. */
  online: boolean;
  /** It had been taken down, and it is back. */
  cameBack: boolean;
  /** Listed in a search engine's results and not yet delisted. */
  inGoogle: boolean;
  removedFromGoogle: boolean;
  firstSeen: string;
}

/** One site, one case: what the live screen lists. */
export interface LiveSite {
  /** The registrable domain, or its masked form when the site is locked. */
  domain: string;
  locked: boolean;
  /** How sure we are it is her content, 0–100; null until a page is checked. */
  match: number | null;
  /** A page in Google results, or an account using her name: the ones that hurt most. */
  hot: boolean;
  impersonation: boolean;
  linkCount: number;
  inGoogle: number;
  /** Empty when locked: which pages is what a subscription pays to see. */
  links: LiveLink[];
  firstSeen: string;
  step: 'found' | 'processing' | 'delisting' | 'removed';
}

/** A search, running or finished, as the live screen narrates it. */
export interface LiveScan {
  state: 'none' | 'running' | 'complete' | 'failed';
  runId: string | null;
  startedAt: string | null;
  /** Search engines asked plus sites walked, so far and in all. */
  placesChecked: number;
  placesTotal: number;
  engines: number;
  sites: number;
  /** When the plan searches again; null without a plan. */
  nextAt: string | null;
}

export interface LiveHistoryRun {
  id: string;
  startedAt: string;
  mode: string | null;
  found: number;
  cameBack: number;
  removed: number;
  placesChecked: number;
}

/** GET /detection/live: everything the Overview draws, for one profile. */
export interface LiveSnapshot {
  profile: { id: string; stageName: string; usernames: string[]; photos: number; names: number; followerRange: string | null;
    /** Whether the first handle names a real OnlyFans page; null while unchecked or the platform did not answer. */
    handleExists: boolean | null;
  };
  paying: boolean;
  /** Sites shown by name before a subscription; the rest are locked. */
  visibleSites: number;
  scan: LiveScan;
  sites: LiveSite[];
  totals: { links: number; sites: number; inGoogle: number; online: number };
  exposure: ExposureCounts;
  history: LiveHistoryRun[];
}

/** One creator's month, in an agency's consolidated report. */
export interface PortfolioReportRow {
  profileId: string;
  stageName: string;
  managerName: string | null;
  found: number;
  removedAtSource: number;
  delisted: number;
  cameBack: number;
  stillOnline: number;
}

/** GET /account/reports/monthly: every creator's month, and who the report is from. */
export interface PortfolioReport {
  month: string;
  /** The agency's own name at the top; the product's name only without white-label. */
  from: string;
  whiteLabel: boolean;
  rows: PortfolioReportRow[];
  totals: Omit<PortfolioReportRow, 'profileId' | 'stageName' | 'managerName'>;
}

/** What Domains info asks of the whole list, answered by the database. */
export interface DomainPageQuery {
  quick?: 'noContact' | 'all' | 'hasContact';
  q?: string;
  domains?: string[];
  hoster?: string;
  source?: string;
  load?: 'some' | 'none';
  hideMirrors?: boolean;
  /** The directory's CDN word: 'Cloudflare', 'Cloudflare DNS only', or 'none'. */
  cdn?: string;
  /** SiteCategory, SiteSearchState, the registrar's name ('unknown' for none), DnsState ('none' unchecked), the list a domain came from. */
  kind?: string;
  search?: string;
  registrar?: string;
  /** One EPP status word (DEC-044): matched against any entry in the
   *  domain's own `registryStatus` array, not a code we collapse ourselves. */
  status?: string;
  dns?: string;
  list?: string;
  sort?: string;
  dir?: 'asc' | 'desc';
  page?: number;
  size?: number;
}

/** One page of Domains info, and what the whole list holds. */
export interface DomainPage {
  rows: DomainInfoRecord[];
  total: number;
  counts: { all: number; noContact: number; hasContact: number; mirrors: number };
  facets: {
    hosters: Array<{ value: string; count: number }>;
    sources: Array<{ value: string; count: number }>;
    loads: Array<{ value: string; count: number }>;
    cdns: Array<{ value: string; count: number }>;
    kinds: Array<{ value: string; count: number }>;
    searches: Array<{ value: string; count: number }>;
    registrars: Array<{ value: string; count: number }>;
    dns: Array<{ value: string; count: number }>;
    lists: Array<{ value: string; count: number }>;
  };
}

/** One paid WHOIS request as the WHOIS history tab lists it. */
export interface WhoisLookupRecord {
  id: string;
  domain: string;
  askedAt: string;
  provider: string;
  httpStatus: number | null;
  outcome: WhoisOutcome;
  email: string | null;
  registrar: string | null;
  quotaLeft: number | null;
  quotaLimit: number | null;
  durationMs: number | null;
  reason: WhoisReason;
  /** From the stored answer: the zone, when the domain was registered and runs out,
   *  whose name servers it uses, and whether the registry has it on hold. */
  zone: string;
  created: string | null;
  expires: string | null;
  dns: string | null;
  onHold: boolean;
  /** Every status the registry reported, as EPP codes, so support reads the
   *  whole picture rather than just the on-hold flag. */
  registryStatus: string[];
}

/** A page of the WHOIS history, and how many of every outcome there are in all. */
export interface WhoisLookupPage {
  rows: WhoisLookupRecord[];
  total: number;
  /** Counts over the whole log, for the filters above the table. */
  facets: Record<'outcomes' | 'reasons' | 'registrars' | 'zones' | 'dns' | 'holds' | 'statuses' | 'providers', Array<{ value: string; count: number }>>;
  prefill: WhoisPrefillProgress;
}

/** Where the catalogue run through WHOIS stands (DEC-035). */
export interface WhoisPrefillProgress {
  on: boolean;
  /** Lookups it may spend in all; null for the whole list. */
  limit: number | null;
  /** Lookups it has spent, answered ones only: a timeout costs nothing. */
  spent: number;
  total: number;
  /** Ruled out by DNS: gone, no address, or no DNS answer. */
  dead: number;
  asked: number;
  withAddress: number;
  /** Asked, still without an address, and waiting for RDAP (DEC-037). */
  rdapLeft: number;
  left: number;
}

/** One request with the answer exactly as the service sent it. */
export interface WhoisLookupDetail extends Omit<WhoisLookupRecord, 'zone' | 'created' | 'expires' | 'dns' | 'onHold'> {
  response: unknown;
  headers: unknown;
}

/**
 * One thing the account has to do before something can proceed. Derived from
 * state on every ask, so it lasts exactly as long as the problem does and two
 * messages about one problem are still one action.
 */
export interface OutstandingAction {
  /** Stable per problem: `authorization:<profileId>`, `billing`, `profile:<id>`. */
  key: string;
  kind: 'authorization' | 'billing' | 'profile';
  title: string;
  body: string;
  href: string;
  label: string;
}

/** One week of signups, Monday-anchored, with how many of them have since paid.
 *  `converted` is a cumulative state read now, not a cohort frozen that week:
 *  a signup that paid months later counts here, so the bar reads "how this
 *  week's intake is doing", not "who paid in week one". */
export interface SignupsWeek {
  /** ISO date (YYYY-MM-DD) of the Monday this week starts on. */
  weekStart: string;
  total: number;
  converted: number;
}

/** One bar of a breakdown: a language, a country or a device, with its count. */
export interface SignupsBreakdownRow {
  /** Stable machine value: a language subtag, an ISO country, a device name. */
  key: string;
  /** What the panel prints, already humanised. */
  label: string;
  count: number;
}

/** The acquisition and conversion picture for the whole account base, read-only.
 *  Demo and deleted accounts are left out: they are not acquisition. */
export interface SignupsSummary {
  total: number;
  converted: number;
  /** Paid over total, 0-100 with one decimal. */
  conversionPct: number;
  /** Accounts created since the first of the current calendar month. */
  newThisMonth: number;
  referred: number;
  organic: number;
  /** Referred over total, 0-100 with one decimal. */
  referredSharePct: number;
  /** Oldest week first, last ~12 weeks ending with the current one. */
  weeks: SignupsWeek[];
  languages: SignupsBreakdownRow[];
  countries: SignupsBreakdownRow[];
  devices: SignupsBreakdownRow[];
  /** Weeks the period covers: 4, 12 or 26. `weeks` and everything under it is this period. */
  rangeWeeks: number;
  /** What the figures were narrowed to, or null for every account. */
  filter: { source: string | null; device: string | null; country: string | null } | null;
  /** The people who registered in the period. */
  period: SignupsPeriod;
  /** The same number of weeks straight before, or null when nobody registered then. */
  previous: SignupsPeriod | null;
  /** The onboarding steps the period's signups got through, in order. */
  funnel: SignupsFunnelStep[];
  /** Accounts that registered over a week ago and stopped part-way. */
  stalled: { noProfile: number; noLetter: number; notPaid: number };
  /** What the filters can be narrowed to, counted over every account. */
  facets: { countries: SignupsBreakdownRow[]; devices: SignupsBreakdownRow[] };
}

/** How many registered in a stretch of weeks, and how many of those paid. */
export interface SignupsPeriod {
  signedUp: number;
  paid: number;
  /** Paid over signed up, 0-100 with one decimal. */
  conversionPct: number;
}

export type SignupsFunnelKey = 'signed_up' | 'profile' | 'scanning' | 'letter' | 'paid';

/** One step of onboarding and how many of the period's signups reached it. */
export interface SignupsFunnelStep {
  key: SignupsFunnelKey;
  count: number;
}

/** One row of the case-status breakdown: how many cases sit in each status. */
export interface TakedownHealthStatusCount {
  status: DomainCaseStatus;
  count: number;
}

/** A group of open cases held up for the same reason, worst-first. */
export interface TakedownHealthStuck {
  /** Machine key, e.g. `no_abuse_address`. */
  reason: string;
  /** What support reads: "No abuse address". */
  label: string;
  count: number;
}

/** One open case on the needs-attention list, with where it opens. */
export interface TakedownHealthOpenCase {
  caseId: string;
  ref: number;
  domain: string;
  /** Client name, falling back to login email then account id. */
  client: string;
  accountId: string;
  status: DomainCaseStatus;
  /** Whole days since the case opened. */
  ageDays: number;
}

/** One week of the takedown flow: cases that opened and cases that ended removed. */
export interface TakedownHealthWeek {
  /** ISO date (YYYY-MM-DD) of the Monday this week starts on. */
  weekStart: string;
  opened: number;
  removed: number;
  /** Cases still open at the end of this week. */
  open: number;
}

/**
 * The product's core promise, measured across every client at once: how fast
 * removals land, how often they succeed, and what is stuck. Read-only.
 */
export interface TakedownHealth {
  /** Cases still in play (pending, hold, in_progress). */
  openCount: number;
  /** Cases that will never move again (done, declined, cancelled). */
  resolvedCount: number;
  /** Closed with everything removed. */
  removedCount: number;
  /** Removed over (removed + declined), 0-1; null when neither has happened. */
  successRate: number | null;
  /** Days from opened to resolved for removed cases; null when none yet. */
  medianDaysToRemoval: number | null;
  avgDaysToRemoval: number | null;
  byStatus: TakedownHealthStatusCount[];
  stuckByReason: TakedownHealthStuck[];
  /** Oldest open cases first, capped. */
  oldestOpen: TakedownHealthOpenCase[];
  /** Oldest week first, ending with the current one; as many as the reader picked. */
  weeks: TakedownHealthWeek[];
  generatedAt: string;
  /** Weeks the period covers: 4, 12 or 26. */
  rangeWeeks: number;
  /** What the figures were narrowed to, or null for every account. */
  filter: { plan: string | null; kind: string | null } | null;
  /** The cases opened in the period, and what became of them. */
  period: TakedownHealthPeriod;
  /** The same number of weeks straight before, or null when no case opened then. */
  previous: TakedownHealthPeriod | null;
  /** The slowest removal, as the day count 9 in 10 removals beat; null until some are done. */
  p90DaysToRemoval: number | null;
  /** How many removals landed within the target, 0-1; null when none have landed. */
  withinTargetRate: number | null;
  /** The days a removal is meant to land within. */
  targetDays: number;
  /** Open cases that have waited longer than the target. */
  overdueCount: number;
  /** Open cases, by how long they have waited. */
  ageBuckets: TakedownHealthBucket[];
  /** Removed cases, by how long the removal took. */
  removalBuckets: TakedownHealthBucket[];
  /** The hosts with the most cases, and how each of them behaves. */
  byDomain: TakedownHealthDomain[];
}

/** A removal is meant to land within this many days; the screens count what is slower as overdue. */
export const TAKEDOWN_TARGET_DAYS = 7;

/** What became of the cases opened in a stretch of weeks. */
export interface TakedownHealthPeriod {
  opened: number;
  removed: number;
  declined: number;
  /** Removed over (removed + declined), 0-1; null when neither has happened. */
  successRate: number | null;
  /** Days from opened to removed, middle value; null when none removed. */
  medianDays: number | null;
}

/** One band of a distribution, in days. */
export interface TakedownHealthBucket {
  key: string;
  label: string;
  count: number;
  /** The band's lower edge in days, so a screen can colour by age. */
  fromDays: number;
}

/** One host: how many cases against it and how it answers. */
export interface TakedownHealthDomain {
  domain: string;
  total: number;
  open: number;
  removed: number;
  declined: number;
  /** Days from opened to removed, middle value; null when none removed. */
  medianDays: number | null;
}

/** One plan's share of the money: how many pay for it, and the MRR they add. */
export interface RevenuePlanBreakdown {
  /** The live plan a legacy code is served as, never the retired code itself. */
  planCode: PlanCode;
  label: string;
  activeCount: number;
  /** Monthly recurring revenue from this plan, annual lines normalised to /12. */
  mrrUsd: number;
}

/** Collected revenue for one calendar month, from paid Stripe events. */
export interface RevenueTrendPoint {
  /** 'YYYY-MM', UTC. */
  month: string;
  collectedUsd: number;
}

/**
 * The SaaS money health across every account, for GET /admin/revenue. MRR is
 * valued from plan prices (annual lines divided by twelve); the trend is the
 * cash a month's paid invoices actually collected, which is why a month with an
 * annual renewal reads higher than steady MRR rather than wrong.
 */
export interface RevenueSummary {
  mrrUsd: number;
  /** MRR times twelve, so the page need not multiply. */
  arrUsd: number;
  activeSubscriptions: number;
  /** MRR over active subscriptions; 0 when there are none. */
  arpuUsd: number;
  newThisMonth: number;
  churnedThisMonth: number;
  /** Churned over (active + churned) this month, as a percentage 0-100. */
  churnRatePct: number;
  monthlySubscriptions: number;
  annualSubscriptions: number;
  byPlan: RevenuePlanBreakdown[];
  /** Oldest month first, one point per month of the window the reader picked. */
  trend: RevenueTrendPoint[];
  generatedAt: string;
  /** Months the cash trend covers: 3, 6 or 12. */
  rangeMonths: number;
  /** The plan every figure was narrowed to, or null for all of them. */
  filter: { plan: string } | null;
  /** How many of the active subscriptions are still on a free trial; MRR counts them. */
  trialing: number;
  /** Accounts whose last payment failed: revenue that is owed, not yet received. Not in MRR. */
  pastDue: RevenueAtRisk;
  /** Active accounts that have told Stripe not to renew; they leave at the end of the period. */
  cancelling: RevenueAtRisk;
  /** MRR added by the accounts whose billing started this calendar month. */
  newMrrUsd: number;
}

/** A group of accounts, and the monthly revenue riding on them. */
export interface RevenueAtRisk {
  accounts: number;
  mrrUsd: number;
}

/** One paid profile whose last scan is older than the promised cadence. */
export interface ScanHealthOverdue {
  profileId: string;
  stageName: string;
  accountId: string;
  /** Client name, falling back to login email then account id. */
  accountLabel: string;
  /** The path to the client's own page, so a row is one click from acting. */
  clientLink: string;
  /** Last successful or attempted scan, ISO; null when never scanned. */
  lastScanAt: string | null;
  /** Whole days past the cadence deadline, worst first. */
  daysOverdue: number;
}

/** A scan that ended in failure inside the recent window, newest first. */
export interface ScanHealthFailure {
  scanId: string;
  profileId: string;
  stageName: string;
  accountId: string;
  accountLabel: string;
  clientLink: string;
  /** Why it failed, in the words the failure used; null when none recorded. */
  failureReason: string | null;
  /** When the run finished, or started when it never finished. ISO. */
  failedAt: string;
}

/**
 * The scan promise measured across every paid profile: are clients searched
 * as often as their plan says, and what broke recently. Read-only. "Overdue"
 * is a profile whose last scan is older than the rescan cadence
 * (`SEARCH_EVERY_DAYS`), the same number the scheduler searches on.
 */
export interface ScanHealth {
  /** Paid profiles on record, the set every figure is measured over. */
  profilesTotal: number;
  /** How many of those are past their rescan deadline. */
  profilesOverdue: number;
  /** Scans that failed inside the recent window. */
  failedRecently: number;
  /** The rescan cadence in days, so the page can show the SLA it checks. */
  everyDays: number;
  /** How many days back the failure window reaches. */
  recentWindowDays: number;
  /** Worst-overdue profiles first, capped. */
  overdue: ScanHealthOverdue[];
  /** Newest failures first, capped. */
  recentFailures: ScanHealthFailure[];
  generatedAt: string;
}

/**
 * The evidence a bank reads when a client disputes a charge. Field names
 * follow Stripe's own `dispute.evidence`; empty string means "not given".
 */
export interface DisputeEvidence {
  customerName: string;
  customerEmailAddress: string;
  serviceDate: string;
  productDescription: string;
  accessActivityLog: string;
  refundPolicy: string;
  cancellationPolicyDisclosure: string;
  uncategorizedText: string;
}

/** One chargeback, on the admin Disputes page. Stripe is the record; nothing is copied into our database. */
export interface DisputeRow {
  id: string;
  chargeId: string;
  amountUsd: number;
  reason: string;
  /** Stripe's own status: needs_response, under_review, won, lost, and the warning_* forms. */
  status: string;
  /** The last moment evidence is accepted; null once the window has closed. */
  respondBy: string | null;
  /** Evidence has been sent to the bank and can no longer be changed. */
  submitted: boolean;
  createdAt: string;
  accountId: string | null;
  accountLabel: string | null;
}

export interface DisputeDetail extends DisputeRow {
  stripeUrl: string;
  /** What is saved in Stripe right now. */
  evidence: DisputeEvidence;
  /** What our own records say about this client, to start from. */
  suggested: DisputeEvidence;
}

export interface DisputeEvidenceInput extends Partial<DisputeEvidence> {
  /** false saves a draft in Stripe; true sends it to the bank, once. */
  submit: boolean;
}

/** One letter sent to a client's address, on the admin Emails sent screen. */
export interface ClientEmailRow {
  id: string;
  toEmail: string;
  subject: string;
  /** The plain text as it was sent. Empty in the list; the row's own fetch carries it. */
  body: string;
  /** The HTML part exactly as sent, empty in the list and for letters recorded before it was kept. */
  html: string;
  /** The first line or two of the text, so the list says what the letter was about. */
  preview: string;
  sentAs: string;
  /** The From line as the recipient reads it: display name, then address. Empty for letters recorded before it was kept. */
  sentFrom: string;
  status: 'sent' | 'failed';
  error: string | null;
  /** Caught by the test inbox: it never left the building. */
  sandbox: boolean;
  createdAt: string;
  /** The account that owns the address, when one does. */
  accountId: string | null;
  accountLabel: string | null;
  /** A creator on her own, or an agency. Null when no account owns the address. */
  accountKind: 'solo' | 'agency' | null;
}

/** One entry of the audit trail, on the admin Audit log screen. */
export interface AuditLogRow {
  id: string;
  createdAt: string;
  /** Who did it: their email and role, or null for the system. */
  actorEmail: string | null;
  actorRole: string | null;
  /** The account it was done in or to, when there is one. */
  accountId: string | null;
  accountLabel: string | null;
  accountKind: 'solo' | 'agency' | null;
  /** `POST /profiles/:id/whitelist` for a call, or our own word (`account.plan`) for a change with a before and after. */
  action: string;
  /** A call the person made, as opposed to a change we described. */
  isCall: boolean;
  was: string | null;
  became: string | null;
  /** Support signed in as the client and did this, or signing in as her was the entry itself. */
  viaSupport: boolean;
  /** The HTTP status a call ended with. Null for a described change. */
  status: number | null;
  ip: string | null;
}

/** One thing the product tried to send and could not, on the admin Delivery log. */
export interface DeliveryRow {
  id: string;
  /** `slack`, `slack-webhook` or `email`. */
  via: string;
  target: string;
  summary: string;
  error: string;
  attempts: number;
  createdAt: string;
  resolvedAt: string | null;
  /** Can be sent again from here. A failed letter cannot: it is seen, and answered, on Emails sent. */
  canRetry: boolean;
}

/** Who has access to the panel and what has been tried against it lately, on the admin Security screen. */
export interface SecurityReport {
  staff: Array<{
    id: string;
    email: string;
    name: string | null;
    role: string;
    disabled: boolean;
    lastLoginAt: string | null;
    lastActiveAt: string | null;
    /** Times they signed in as a client in the last thirty days. */
    asClient30d: number;
  }>;
  /** Refused sign-ins in the last day, for the badge in the head. */
  failedLast24h: number;
  /** Newest first: colleagues inside a client's account, refused sign-ins, secrets shown. */
  events: Array<{
    id: string;
    when: string;
    kind: 'as-client' | 'failed-sign-in' | 'secret';
    /** The colleague, or the address typed into a refused sign-in. */
    who: string | null;
    accountId: string | null;
    accountLabel: string | null;
    ip: string | null;
    /** The reason given, the route, or what was typed. */
    detail: string;
    /** False when the attempt was turned away. */
    allowed: boolean;
  }>;
}
